import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Upload as UploadAntd,
  Avatar,
  Divider,
  List,
  Tag,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  UserOutlined,
  SearchOutlined,
  PhoneOutlined,
  MailOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import useDebounce from "../hooks/useDebounce";
import TextArea from "antd/lib/input/TextArea";
import { UploadCloud } from "lucide-react";

const { Search } = Input;
const { Title, Text } = Typography;

function ReportManager() {
  const { request } = useRequest();
  const requestRef = useRef(request);
  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  // ---------- Patient search & selection ----------
  const [searchMobile, setSearchMobile] = useState("");
  const debouncedMobile = useDebounce(searchMobile, 500);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);

  // ---------- Reports list ----------
  const [reports, setReports] = useState([
    {
      file: null,
      category_id: null,
      file_name: "",
      description: "",
      notes: "",
    },
  ]);
  const [submitting, setSubmitting] = useState(false);

  // ---------- Categories ----------
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // ---------- Fetch categories once ----------
  useEffect(() => {
    const fetchCategories = () => {
      setLoadingCategories(true);
      requestRef.current({
        url: `/api/admin/document-categories?status=ACTIVE`,
        method: "GET",
        onSuccess: (data) => {
          setLoadingCategories(false);
          const fetchedCategories = data?.data || [];
          setCategories(fetchedCategories);
          if (fetchedCategories.length > 0) {
            setReports((prev) => {
              if (prev.length === 1 && prev[0].category_id === null) {
                const updated = [...prev];
                updated[0].category_id = fetchedCategories[0].id;
                return updated;
              }
              return prev;
            });
          }
        },
        onError: () => {
          setLoadingCategories(false);
          ShowToast("Failed to load document categories", Severty.ERROR);
        },
      });
    };
    fetchCategories();
  }, []);

  const getDefaultCategoryId = () =>
    categories.length > 0 ? categories[0].id : null;

  // ---------- Fetch patients when debouncedMobile changes ----------
  useEffect(() => {
    const fetchPatients = (searchTerm) => {
      if (!searchTerm || searchTerm.length < 3) {
        setPatients([]);
        setSearchDropdownOpen(false);
        return;
      }
      setLoadingPatients(true);
      requestRef.current({
        url: `/lab/patients_list?search=${encodeURIComponent(searchTerm)}&limit=20`,
        method: "GET",
        onSuccess: (data) => {
          setLoadingPatients(false);
          let patientsData = [];
          if (data?.data && Array.isArray(data.data)) {
            patientsData = data.data;
          } else if (Array.isArray(data)) {
            patientsData = data;
          }
          setPatients(patientsData);
          setSearchDropdownOpen(true);
        },
        onError: () => {
          setLoadingPatients(false);
          setPatients([]);
          ShowToast("Failed to search patients", Severty.ERROR);
        },
      });
    };

    if (debouncedMobile) {
      fetchPatients(debouncedMobile);
    } else {
      setPatients([]);
      setSearchDropdownOpen(false);
    }
  }, [debouncedMobile]); // ✅ no dependency on request or fetchPatients

  // Select patient
  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setSearchDropdownOpen(false);
    setSearchMobile("");
    setReports([
      {
        file: null,
        category_id: getDefaultCategoryId(),
        file_name: "",
        description: "",
        notes: "",
      },
    ]);
  };

  // Clear patient
  const handleClearPatient = () => {
    setSelectedPatient(null);
    setReports([
      {
        file: null,
        category_id: getDefaultCategoryId(),
        file_name: "",
        description: "",
        notes: "",
      },
    ]);
  };

  // Add more report
  const handleAddMore = () => {
    setReports([
      ...reports,
      {
        file: null,
        category_id: getDefaultCategoryId(),
        file_name: "",
        description: "",
        notes: "",
      },
    ]);
  };

  // Remove report
  const handleRemoveReport = (index) => {
    if (reports.length === 1) {
      ShowToast("At least one report is required", Severty.WARNING);
      return;
    }
    const newReports = [...reports];
    newReports.splice(index, 1);
    setReports(newReports);
  };

  // Update report field
  const handleReportChange = (index, field, value) => {
    const updated = [...reports];
    updated[index][field] = value;
    setReports(updated);
  };

  // File upload
  const handleFileChange = (index, fileList) => {
    const file = fileList.length > 0 ? fileList[0].originFileObj : null;
    handleReportChange(index, "file", file);
    if (file && !reports[index].file_name) {
      handleReportChange(index, "file_name", file.name);
    }
  };

  // Submit all reports
  const handleSubmitAll = async () => {
    if (!selectedPatient) {
      ShowToast("Please select a patient first", Severty.ERROR);
      return;
    }
    for (let i = 0; i < reports.length; i++) {
      if (!reports[i].file) {
        ShowToast(`Report ${i + 1} has no file uploaded`, Severty.ERROR);
        return;
      }
    }

    setSubmitting(true);
    const reportsPayload = reports.map((r) => ({
      customer_id: selectedPatient.id,
      customer_name: selectedPatient.full_name,
      customer_email: selectedPatient.email,
      country_code: selectedPatient.country_code,
      customer_mobile: selectedPatient.mobile,
      category_id: r.category_id || null,
      report_file_name: r.file_name || null,
      description: r.description || "",
      notes: r.notes || "",
    }));

    const formData = new FormData();
    formData.append("reports", JSON.stringify(reportsPayload));
    reports.forEach((r, idx) => {
      if (r.file) formData.append(`file_${idx}`, r.file);
    });

    requestRef.current({
      url: `/lab/reports/bulk/minimal`,
      method: "POST",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
      onSuccess: () => {
        ShowToast("All reports uploaded successfully", Severty.SUCCESS);
        setSelectedPatient(null);
        setReports([
          {
            file: null,
            category_id: getDefaultCategoryId(),
            file_name: "",
            description: "",
            notes: "",
          },
        ]);
        setSubmitting(false);
      },
      onError: (error) => {
        ShowToast(
          error.response?.data?.message || "Failed to upload reports",
          Severty.ERROR,
        );
        setSubmitting(false);
      },
    });
  };

  const patientOptions = patients.map((p) => ({
    value: p.id,
    label: `${p.full_name} (${p.mobile})`,
    patient: p,
  }));

  const categoryOptions = categories.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  return (
    <div style={{ padding: "24px", background: "#f8fafc", minHeight: "100vh" }}>
      {/* Top Header Card */}

      {!selectedPatient && (
        <>
          <Card
            bordered={false}
            style={{
              borderRadius: 12,
              boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
              marginBottom: 24,
            }}
            bodyStyle={{ padding: "24px" }}
          >
            <Space size={16} align="center">
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: "#3b82f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 24,
                  boxShadow: "0 8px 16px rgba(59, 130, 246, 0.3)",
                }}
              >
                <UserOutlined />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Title
                  level={3}
                  style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}
                >
                  Report Upload
                </Title>
                <Text style={{ color: "#64748b", fontSize: 14 }}>
                  Search a patient to upload and manage their medical reports
                </Text>
              </div>
            </Space>
          </Card>
          <Card
            bordered={false}
            style={{
              borderRadius: 12,
              boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
            }}
            bodyStyle={{ padding: 0 }}
          >
            {/* STEP 1: Search Patient */}

            <>
              <div
                style={{
                  padding: "16px 24px",
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "#e0f2fe",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 12,
                    color: "#0284c7",
                  }}
                >
                  <SearchOutlined style={{ fontSize: 14 }} />
                </div>
                <Text
                  strong
                  style={{
                    color: "#334155",
                    fontSize: 13,
                    letterSpacing: "0.5px",
                  }}
                >
                  SEARCH PATIENT
                </Text>
              </div>

              <div
                style={{
                  padding: "40px 24px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  background: "#fff",
                  borderRadius: "0 0 12px 12px",
                }}
              >
                <div
                  style={{ width: "100%", maxWidth: 600, position: "relative" }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <Text strong style={{ color: "#334155" }}>
                      Mobile Number
                    </Text>
                  </div>
                  <Search
                    className="search-patient-input12"
                    placeholder="Enter mobile number..."
                    value={searchMobile}
                    onChange={(e) => setSearchMobile(e.target.value)}
                    prefix={
                      <PhoneOutlined
                        style={{ color: "#94a3b8", marginRight: 8 }}
                      />
                    }
                    enterButton={
                      <Button
                        type="primary"
                        style={{
                          background: "#22d3ee",
                          borderColor: "#22d3ee",
                          borderRadius: "0 8px 8px 0",
                          height: 50,
                          padding: "0 24px",
                          fontWeight: 600,
                        }}
                      >
                        <SearchOutlined />
                      </Button>
                    }
                    size="large"
                  />

                  {searchDropdownOpen && patients.length > 0 && (
                    <Select
                      showSearch
                      placeholder="Select patient"
                      style={{ width: "100%", marginTop: 8 }}
                      options={patientOptions}
                      onSelect={(val, option) =>
                        handleSelectPatient(option.patient)
                      }
                      open={searchDropdownOpen}
                      loading={loadingPatients}
                    />
                  )}

                  <div style={{ marginTop: 16, textAlign: "center" }}>
                    <Text style={{ color: "#94a3b8", fontSize: 13 }}>
                      Enter the registered 10-digit mobile number to fetch
                      patient details
                    </Text>
                  </div>
                </div>
              </div>
            </>
          </Card>
        </>
      )}

      {/* STEP 2 & 3: Selected Patient & Reports */}
      {selectedPatient && (
        <div>
          {/* Patient Info Card */}
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              marginBottom: 24,
              boxShadow: "0 4px 20px rgba(0,0,0,0.02)",
            }}
            bodyStyle={{ padding: "20px 24px" }}
          >
            <Row align="middle" justify="space-between">
              <Col>
                <Space size={16}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      background: "#1677ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: 24,
                    }}
                  >
                    <UserOutlined />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Title
                      level={5}
                      style={{ margin: 0, color: "#1e293b", fontSize: 16 }}
                    >
                      {selectedPatient.full_name}
                    </Title>
                    <Space size={16} style={{ marginTop: 4 }}>
                      <Text style={{ color: "#64748b", fontSize: 13 }}>
                        <MailOutlined style={{ marginRight: 6 }} />
                        {selectedPatient.email}
                      </Text>
                      <Text style={{ color: "#64748b", fontSize: 13 }}>
                        <PhoneOutlined style={{ marginRight: 6 }} />
                        {selectedPatient.country_code} {selectedPatient.mobile}
                      </Text>
                    </Space>
                  </div>
                </Space>
              </Col>

              <Col>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={handleClearPatient}
                  style={{
                    borderRadius: 20,
                    color: "#1677ff",
                    borderColor: "#bae0ff",
                    background: "#e6f4ff",
                    fontWeight: 500,
                    padding: "0 16px",
                  }}
                >
                  Change Patient
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Header for Reports */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
              padding: "0 8px",
            }}
          >
            <Space>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 4,
                  background: "#e6f4ff",
                  color: "#1677ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                }}
              >
                <FileTextOutlined />
              </div>
              <Text
                strong
                style={{
                  color: "#334155",
                  fontSize: 13,
                  letterSpacing: "0.5px",
                }}
              >
                UPLOAD REPORTS
              </Text>
            </Space>
            <Text style={{ color: "#64748b", fontSize: 13 }}>
              {reports.length} report{reports.length !== 1 ? "s" : ""}
            </Text>
          </div>

          {/* Reports List */}
          {reports.map((item, idx) => (
            <Card
              key={idx}
              bordered={false}
              style={{
                borderRadius: 16,
                marginBottom: 16,
                boxShadow: "0 4px 20px rgba(0,0,0,0.02)",
                border: "1px solid #f1f5f9",
              }}
              bodyStyle={{ padding: 0 }}
            >
              {/* Report Header */}
              <div
                style={{
                  padding: "16px 24px",
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Space>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: "#1677ff",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: 14,
                    }}
                  >
                    {idx + 1}
                  </div>
                  <Text strong style={{ fontSize: 15, color: "#1e293b" }}>
                    Report {idx + 1}
                  </Text>
                </Space>
                <Button
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveReport(idx)}
                />
              </div>

              {/* Report Form */}
              <div style={{ padding: "24px" }}>
                <Form layout="vertical">
                  <Form.Item
                    label={
                      <span style={{ fontWeight: 500, color: "#334155" }}>
                        Upload File <span style={{ color: "#ef4444" }}>*</span>
                      </span>
                    }
                    required={false}
                  >
                    {!item.file ? (
                      <UploadAntd.Dragger
                        accept=".png,.jpg,.jpeg,.webp,.pdf"
                        maxCount={1}
                        beforeUpload={() => false}
                        onChange={({ fileList }) =>
                          handleFileChange(idx, fileList)
                        }
                        style={{
                          background: "#fff",
                          border: "1px dashed #cbd5e1",
                          borderRadius: 12,
                          padding: "24px 0",
                        }}
                      >
                        <p
                          className="ant-upload-drag-icon"
                          style={{ marginBottom: 12 }}
                        >
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              background: "#22d3ee",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              margin: "0 auto",
                              boxShadow: "0 4px 12px rgba(34, 211, 238, 0.4)",
                            }}
                          >
                            <UploadCloud size={24} />
                          </div>
                        </p>
                        <p
                          className="ant-upload-text"
                          style={{
                            color: "#334155",
                            fontSize: 14,
                            fontWeight: 500,
                          }}
                        >
                          <span style={{ color: "#1677ff" }}>
                            Click to upload
                          </span>{" "}
                          or drag and drop
                        </p>
                        <p
                          className="ant-upload-hint"
                          style={{ color: "#94a3b8", fontSize: 12 }}
                        >
                          PDF, JPG, PNG up to 25 MB
                        </p>
                      </UploadAntd.Dragger>
                    ) : (
                      <div
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 12,
                          padding: 16,
                          background: "#f8fafc",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Space size={16}>
                          {item.file.type?.includes("image") ? (
                            <img
                              src={URL.createObjectURL(item.file)}
                              alt="preview"
                              style={{
                                width: 56,
                                height: 56,
                                objectFit: "cover",
                                borderRadius: 8,
                                border: "1px solid #e2e8f0",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 56,
                                height: 56,
                                background: "#e2e8f0",
                                borderRadius: 8,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <FileTextOutlined
                                style={{ fontSize: 24, color: "#64748b" }}
                              />
                            </div>
                          )}
                          <div>
                            <Text
                              strong
                              style={{
                                display: "block",
                                color: "#334155",
                                fontSize: 15,
                              }}
                            >
                              {item.file.name}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                              {item.file.size
                                ? `${(item.file.size / 1024 / 1024).toFixed(2)} MB`
                                : ""}
                            </Text>
                          </div>
                        </Space>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleFileChange(idx, [])}
                          style={{ background: "#fee2e2", borderRadius: 8 }}
                        />
                      </div>
                    )}
                  </Form.Item>

                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item
                        label={
                          <span style={{ fontWeight: 500, color: "#334155" }}>
                            Category
                          </span>
                        }
                      >
                        <Select
                          placeholder="Select category"
                          options={categoryOptions}
                          value={item.category_id}
                          onChange={(val) =>
                            handleReportChange(idx, "category_id", val)
                          }
                          allowClear
                          size="large"
                          style={{ width: "100%" }}
                          className="custom-select-light"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={
                          <span style={{ fontWeight: 500, color: "#334155" }}>
                            File Name
                          </span>
                        }
                      >
                        <Input
                          placeholder="e.g. Blood Report - April 2026"
                          value={item.file_name}
                          onChange={(e) =>
                            handleReportChange(idx, "file_name", e.target.value)
                          }
                          size="large"
                          style={{
                            background: "#f8fafc",
                            borderRadius: 8,
                            borderColor: "#e2e8f0",
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label={
                      <span style={{ fontWeight: 500, color: "#334155" }}>
                        Description
                      </span>
                    }
                  >
                    <TextArea
                      placeholder="Brief description of the report..."
                      rows={3}
                      value={item.description}
                      onChange={(e) =>
                        handleReportChange(idx, "description", e.target.value)
                      }
                      style={{
                        background: "#f8fafc",
                        borderRadius: 8,
                        borderColor: "#e2e8f0",
                        resize: "none",
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <span style={{ fontWeight: 500, color: "#334155" }}>
                        Notes
                      </span>
                    }
                    style={{ marginBottom: 0 }}
                  >
                    <TextArea
                      placeholder="Add any additional notes for the doctor..."
                      rows={3}
                      value={item.notes}
                      onChange={(e) =>
                        handleReportChange(idx, "notes", e.target.value)
                      }
                      style={{
                        background: "#f8fafc",
                        borderRadius: 8,
                        borderColor: "#e2e8f0",
                        resize: "none",
                      }}
                    />
                  </Form.Item>
                </Form>
              </div>
            </Card>
          ))}

          {/* ACTIONS */}
          <div
            style={{
              marginTop: 24,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#fff",
              padding: "16px 24px",
              borderRadius: 16,
              boxShadow: "0 4px 20px rgba(0,0,0,0.02)",
            }}
          >
            <Button
              icon={<PlusOutlined />}
              onClick={handleAddMore}
              style={{
                borderRadius: 20,
                color: "#1677ff",
                borderColor: "#bae0ff",
                background: "#f0f8ff",
                fontWeight: 500,
                padding: "0 20px",
                height: 40,
              }}
            >
              Add Report
            </Button>

            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              size="large"
              onClick={handleSubmitAll}
              loading={submitting}
              style={{
                borderRadius: 20,
                padding: "0 32px",
                height: 40,
                background: "#22d3ee",
                borderColor: "#22d3ee",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(34, 211, 238, 0.4)",
              }}
            >
              Submit All Reports
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportManager;
